/**
 * Mock API Response Registry
 *
 * Define mock responses for API endpoints here.
 * This is where you configure what data to return when real endpoints aren't ready.
 *
 * Usage:
 * 1. Add an entry with the pattern: "METHOD /path/to/endpoint"
 * 2. Supports :param placeholders (e.g., "/hotels/:hotelId/rooms")
 * 3. Define the mock data, optional HTTP status, and optional delay
 *
 * Example:
 * 'GET /api/v2/hotels/:hotelId/rooms': {
 *   data: [{ id: 1, name: 'Deluxe Room' }],
 *   status: 200,  // optional, defaults to 200
 *   delay: 500    // optional, simulates network delay in ms
 * }
 */

export interface MockResponse {
  data: unknown;
  status?: number;
  delay?: number;
  message?: string;
  dynamic?: boolean; // Flag to indicate dynamic data generation
}

export const MOCK_RESPONSES: Record<string, MockResponse> = {
  // ============================================================================
  // Example mock responses - uncomment and modify as needed
  // ============================================================================

  // Example 1: Mock GET request with URL parameters
  // 'GET /api/v2/hotels/:hotelId/rooms': {
  //   data: [
  //     { id: 1, name: 'Deluxe Room', capacity: 2, price: 150 },
  //     { id: 2, name: 'Suite', capacity: 4, price: 300 },
  //     { id: 3, name: 'Standard Room', capacity: 2, price: 100 },
  //   ],
  //   delay: 500, // Simulate 500ms network delay
  // },

  // Example 2: Mock POST request with custom status code
  // 'POST /api/v2/bookings': {
  //   data: {
  //     id: 123,
  //     status: 'confirmed',
  //     createdAt: new Date().toISOString(),
  //     guestName: 'John Doe',
  //     roomId: 1,
  //     checkIn: '2025-12-01',
  //     checkOut: '2025-12-05'
  //   },
  //   status: 201, // Created
  // },

  // Example 3: Mock analytics dashboard data
  // 'GET /api/v2/analytics/dashboard': {
  //   data: {
  //     totalBookings: 150,
  //     revenue: 45000,
  //     occupancyRate: 75,
  //     averageDailyRate: 180,
  //     revenuePerAvailableRoom: 135,
  //   },
  // },

  // Example 4: Mock hotel details
  // 'GET /api/v2/hotels/:hotelId': {
  //   data: {
  //     id: 1,
  //     name: 'Grand Hotel Example',
  //     address: 'Example Street 123',
  //     city: 'Zurich',
  //     country: 'Switzerland',
  //     rating: 4.5,
  //     totalRooms: 50,
  //   },
  //   delay: 300,
  // },

  // Example 5: Mock PUT request (update)
  // 'PUT /api/v2/hotels/:hotelId/rooms/:roomId': {
  //   data: {
  //     id: 1,
  //     name: 'Updated Room Name',
  //     capacity: 3,
  //     price: 175,
  //     updatedAt: new Date().toISOString(),
  //   },
  // },

  // Example 6: Mock DELETE request
  // 'DELETE /api/v2/hotels/:hotelId/rooms/:roomId': {
  //   data: { success: true, message: 'Room deleted successfully' },
  //   status: 204,
  // },

  // ============================================================================
  // Add your mock responses below this line
  // ============================================================================

  // ============================================================================
  // 2. ROOM APIS - USING REAL API CALLS (NO MOCK DATA)
  // ============================================================================
  // Removed mock data for room sorting and visibility to use real API calls



  // ============================================================================
  // 3. DOCUMENTS APIs - USING REAL API CALLS (NO MOCK DATA)
  // ============================================================================
  // Removed mock data for documents to use real API calls

  // ============================================================================
  // 4. ANALYTICS PAGE APIs
  // ============================================================================

  // ============================================================================
  // ROOMS APIs - USING REAL API CALLS (NO MOCK DATA)
  // ============================================================================
  // Removed mock data for 'GET /v2/hotels/:hotelId/rooms' to use real API calls

  // ============================================================================
  // 4. AVAILABILITY & PRICING APIs - USING REAL API CALLS
  // ============================================================================
  // All availability endpoints now use real v2 API calls


  // ============================================================================
  // 5. EXTRA BEDS APIs - USING REAL API CALLS (NO MOCK DATA)
  // ============================================================================
  // Removed mock data for extra-beds to use real API calls
};
